import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useSearch } from '@tanstack/react-router'
import { feature, featureCollection } from '@turf/turf'
import { throttle } from 'es-toolkit'
import type { ColorSpecification, DataDrivenPropertyValueSpecification, FilterSpecification } from 'mapbox-gl'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { Layer, type LayerProps, Source, type VectorSourceSpecification } from 'react-map-gl/mapbox'
import { useActiveOnTimelineLightningAlertState } from '../../data/alerts/useAlerts.ts'
import { isAssetLocation } from '../../data/locations.tsx'
import { useLightningProtections } from '../../data/protections/lightning.ts'
import { type UserPreferences, usePreferences } from '../../data/userProfile.ts'
import { type Dayjs, dayjs } from '../../dayjs.ts'
import { env } from '../../env.ts'
import { useFlags } from '../../launchdarkly.ts'
import { type ThemeMode, useCustomLayers, useLayerInput, useMap, useMapState } from '../../states.ts'
import { useMapTheme } from '../../theme/mapPalette.ts'
import { useAudibleEvents } from '../alerts/audibleEvents.ts'
import { useFilteredLocations } from '../locations/locationUtils.ts'
import { useTimeline } from '../timeline/timelineState.ts'
import { interpolateColorscale, interpolateColorscaleFlat, type LayerId, layerOrder } from './layers.tsx'
import { lightningLayerIcons } from './layerUtils.ts'
import { colorscales, LIGHTNING_ZOOM_BOUNDARY, LRT_ANIMATED_INTERVAL, legendRanges } from './legendConstants.tsx'
import { isLtzShownAt, ltzLayers, ltzRunTime } from './ltz.ts'

const useDebouncedDate = () => {
	const date = useTimeline((s) => s.date)
	const [debouncedDate, setDebouncedDate] = useState(date)
	const throttledSet = useMemo(() => throttle((d: Dayjs) => setDebouncedDate(d), 50, { edges: ['trailing'] }), [])

	useEffect(() => {
		throttledSet(date)
	}, [date, throttledSet])

	useEffect(() => {
		return () => throttledSet.cancel()
	}, [throttledSet])

	return debouncedDate
}

const getAlignedTime = (offset: number, interval: number, units: 's' | 'ms', datetime?: Dayjs) => {
	const conversion = units === 's' ? 1000 : 1 // 1 s = 1000 ms
	const time = datetime?.valueOf() ?? dayjs().valueOf()
	const epoch = Math.floor(time / conversion)
	const rounded = Math.floor((epoch - offset) / interval) * interval
	return dayjs(rounded * conversion)
}

const toTileTimeIso = (datetime: Dayjs) => `${datetime.toISOString().slice(0, -5)}Z` // remove milliseconds

export interface CustomLayerProps {
	isVisible?: boolean
	theme: ThemeMode
	pref?: UserPreferences
	inputs?: Record<string, unknown>
}

type WithoutSuffix<T, Suffix extends string> = T extends `${infer P}${Suffix}` ? P : never

export type DoubleBufferedLayerId = WithoutSuffix<LayerId, '-1'> & WithoutSuffix<LayerId, '-2'>
interface DoubleBufferedSourceProps<TLayerId extends DoubleBufferedLayerId> extends Omit<VectorSourceSpecification, 'tiles' | 'type'> {
	tileUrl: string
	baseLayerId: TLayerId
	forcedRefreshKey: string | boolean
	children: (layerId: LayerId, opacity: number) => ReactNode
}

export const DoubleBufferedSource = <TLayerId extends DoubleBufferedLayerId>({
	tileUrl,
	baseLayerId,
	forcedRefreshKey,
	children,
	...props
}: DoubleBufferedSourceProps<TLayerId>) => {
	const map = useMap((s) => s.map)
	const [activeLayer, setActiveLayer] = useState<1 | 2>(1)
	const prevRefreshKeyRef = useRef(forcedRefreshKey)

	const firstSourceId = `${baseLayerId}-source-1`
	const secondSourceId = `${baseLayerId}-source-2`
	const firstLayerId: LayerId = `${baseLayerId}-1`
	const secondLayerId: LayerId = `${baseLayerId}-2`

	const [firstTileUrl, setFirstTileUrl] = useState<string>(tileUrl)
	const [secondTileUrl, setSecondTileUrl] = useState<string>()
	const [loadingTileUrl, setLoadingTileUrl] = useState<string>()
	const activeTileUrl = activeLayer === 1 ? firstTileUrl : secondTileUrl

	useEffect(() => {
		if (!map) return
		if (tileUrl === activeTileUrl) {
			setLoadingTileUrl(undefined)
			prevRefreshKeyRef.current = forcedRefreshKey
			return
		}

		const isForcedRefresh = forcedRefreshKey !== prevRefreshKeyRef.current
		prevRefreshKeyRef.current = forcedRefreshKey
		// Finish the pending swap before loading a newer URL, unless the refresh is forced.
		if (loadingTileUrl !== undefined && !isForcedRefresh) return

		if (activeLayer === 1) {
			setSecondTileUrl(tileUrl)
		} else {
			setFirstTileUrl(tileUrl)
		}
		setLoadingTileUrl(tileUrl)
	}, [map, activeLayer, activeTileUrl, forcedRefreshKey, loadingTileUrl, tileUrl])

	useEffect(() => {
		if (!map || loadingTileUrl === undefined) return
		const hiddenSourceId = activeLayer === 1 ? secondSourceId : firstSourceId

		const swapActiveLayer = () => {
			setActiveLayer(activeLayer === 1 ? 2 : 1)
			setLoadingTileUrl(undefined)
		}

		// While Mapbox replaces a source's URL it reports the source as loaded with no tiles. The next render requests the new tiles.
		const swapWhenLoaded = () => {
			const source = map.getSource(hiddenSourceId)
			const holdsLoadingTileUrl = source?.type === 'vector' && source.tiles?.[0] === loadingTileUrl
			if (holdsLoadingTileUrl && map.isSourceLoaded(hiddenSourceId)) swapActiveLayer()
		}
		swapWhenLoaded()
		map.on('render', swapWhenLoaded)
		const forceSwapTimeout = setTimeout(() => swapActiveLayer(), 20_000)

		return () => {
			clearTimeout(forceSwapTimeout)
			map.off('render', swapWhenLoaded)
		}
	}, [map, activeLayer, loadingTileUrl, firstSourceId, secondSourceId])

	return (
		<>
			<Source id={firstSourceId} type='vector' scheme='xyz' tiles={[firstTileUrl]} {...props}>
				{children(firstLayerId, activeLayer === 1 ? 1 : 0)}
			</Source>
			{secondTileUrl && (
				<Source id={secondSourceId} type='vector' scheme='xyz' tiles={[secondTileUrl]} {...props}>
					{children(secondLayerId, activeLayer === 2 ? 1 : 0)}
				</Source>
			)}
		</>
	)
}

export const OrderedLayer = ({ id, ...props }: { id: LayerId } & Omit<React.ComponentProps<typeof Layer>, 'id' | 'beforeId'>) => (
	<Layer id={id} beforeId={`z-index-layer-${layerOrder.findIndex((lo) => lo.id === id)}`} {...(props as LayerProps)} />
)

export const LightningDensityLayer = ({ isVisible, theme }: CustomLayerProps) => {
	return (
		<Source
			id='lightning-density-source'
			type='vector'
			minzoom={1}
			maxzoom={5}
			bounds={[-180.0, -90, 180.0, 90]}
			tiles={[`${env.LTG_CONTOUR_LAYER}/world-density-mvt/{z}/{x}/{y}.pbf`]}
		>
			<OrderedLayer
				id='lightning-density'
				type='fill'
				source-layer='dg'
				layout={{
					'fill-sort-key': ['+', ['get', 'density']],
					visibility: isVisible ? 'visible' : 'none',
				}}
				paint={{
					'fill-color': {
						property: 'density',
						// conversions happen at tooltip, not the layer
						stops: interpolateColorscale(colorscales['lightning-density'][theme], ...legendRanges['lightning-density'].KILOMETRES),
					},
					'fill-opacity': 0.2,
				}}
			/>
		</Source>
	)
}

export const LightningGridLayer = ({ isVisible, theme }: CustomLayerProps) => {
	const date = useDebouncedDate()
	const animate = useTimeline((s) => s.isPlaying)
	const getMinutesSinceStart = useTimeline((s) => s.getMinutesSinceStart)
	const { maxAge, cloudToGround } = useLayerInput('lightning-realtime')
	if (date.isAfter(dayjs()) || date.isBefore(dayjs().subtract(7, 'day'))) return
	const alignedTime = getAlignedTime(animate ? 0 : 30, animate ? LRT_ANIMATED_INTERVAL * 60 : 30, 's', date)
	const alignedTimeStr = alignedTime.toISOString()

	const getTileUrl = () => {
		if (cloudToGround && maxAge === 30) return env.LTG_RT_GRID_CG_30_LAYER
		if (cloudToGround) return env.LTG_RT_GRID_CG_LAYER
		if (maxAge === 30) return env.LTG_RT_GRID_30_LAYER
		return env.LTG_RT_GRID_LAYER
	}
	const tileUrl = getTileUrl()

	return (
		<DoubleBufferedSource
			tileUrl={`${tileUrl}?TileMatrix=EPSG:900913:{z}&TileCol={x}&TileRow={y}&TIME=${alignedTimeStr}`}
			baseLayerId='lightning-realtime-grid'
			forcedRefreshKey={`${tileUrl}-${animate}`}
			promoteId={{ LD: 'locHash' }}
		>
			{(layerId, opacity) => (
				<OrderedLayer
					id={layerId}
					maxzoom={LIGHTNING_ZOOM_BOUNDARY}
					type='fill'
					source-layer='LD_agg'
					layout={{ visibility: isVisible ? 'visible' : 'none' }}
					paint={{
						'fill-opacity': opacity,
						'fill-opacity-transition': { duration: 0, delay: 0 },
						'fill-antialias': true,
						'fill-outline-color': 'rgba(255, 255, 0, 0.0)',
						'fill-color': [
							'step',
							['get', 'minAge'],
							colorscales['lightning-realtime'][theme][0], // Base color (before first stop)
							...interpolateColorscaleFlat(colorscales['lightning-realtime'][theme], 0, maxAge, (val) => Math.round(val)),
						],
					}}
					filter={['<', ['to-number', ['get', 'minAge']], getMinutesSinceStart(alignedTime, LRT_ANIMATED_INTERVAL)]}
				/>
			)}
		</DoubleBufferedSource>
	)
}

export const LightningPointLayer = ({ isVisible, theme }: CustomLayerProps) => {
	const date = useDebouncedDate()
	const animate = useTimeline((s) => s.isPlaying)
	const getMinutesSinceStart = useTimeline((s) => s.getMinutesSinceStart)
	const { maxAge, cloudToGround } = useLayerInput('lightning-realtime')
	const now = dayjs()
	if (date.isAfter(now) || date.isBefore(now.subtract(7, 'day'))) return

	const alignedTime = getAlignedTime(animate ? 0 : 30, animate ? LRT_ANIMATED_INTERVAL * 60 : 30, 's', date)
	const alignedTimeStr = alignedTime.toISOString()

	const colorStyle: DataDrivenPropertyValueSpecification<ColorSpecification> = [
		'step',
		['get', 'age'],
		colorscales['lightning-realtime'][theme][0], // Base color (before first stop)
		...interpolateColorscaleFlat(colorscales['lightning-realtime'][theme], 0, maxAge, (val) => Math.round(val)),
	]

	const opacityStyle = (opacity: number): DataDrivenPropertyValueSpecification<number> => [
		'step',
		['zoom'],
		0.01,
		LIGHTNING_ZOOM_BOUNDARY, // Step at zoom level (LIGHTNING_ZOOM_BOUNDARY)
		opacity, // Opacity when zoom >= (LIGHTNING_ZOOM_BOUNDARY)
	]

	const filter: FilterSpecification = [
		'all',
		...(cloudToGround ? [['!=', ['get', 'classification'], 'C']] : []),
		['<=', ['to-number', ['get', 'age']], maxAge],
		['<', ['to-number', ['get', 'age']], getMinutesSinceStart(alignedTime, LRT_ANIMATED_INTERVAL)],
	]

	const visibility = isVisible ? 'visible' : 'none'
	return (
		<DoubleBufferedSource
			baseLayerId='lightning-realtime-point'
			forcedRefreshKey={animate}
			tileUrl={
				`${env.LTG_RT_POINT_LAYER}?layer=lightning:LD&tilematrixset=EPSG:900913&` +
				'Service=WMTS&Request=GetTile&Version=1.0.0&Format=application/vnd.mapbox-vector-tile&' +
				`TileMatrix=EPSG:900913:{z}&TileCol={x}&TileRow={y}&TIME=${alignedTimeStr}`
			}
			key={cloudToGround ? 'cg' : 'all'}
			promoteId={{ LD: 'locHash' }}
			minzoom={LIGHTNING_ZOOM_BOUNDARY}
			maxzoom={LIGHTNING_ZOOM_BOUNDARY + 1}
		>
			{(layerId, opacity) => (
				<OrderedLayer
					id={layerId}
					source-layer='LD'
					{...(cloudToGround
						? {
								type: 'symbol',
								paint: {
									'icon-color': colorStyle,
									'icon-opacity': opacityStyle(opacity),
									'icon-opacity-transition': { duration: 0, delay: 0 },
								},
								layout: {
									'icon-image': [
										'image',
										[
											'case',
											['==', ['get', 'classification'], '+'],
											lightningLayerIcons.positive,
											['==', ['get', 'classification'], '-'],
											lightningLayerIcons.negative,
											'.',
										],
										{ params: { iconColor: colorStyle } },
									],
									'icon-size': 1.4,
									'icon-allow-overlap': true,
									'icon-ignore-placement': true,
									visibility,
								},
								filter,
							}
						: {
								type: 'circle',
								paint: {
									'circle-color': colorStyle,
									'circle-radius': [
										'case',
										['==', ['get', 'classification'], 'C'],
										3,
										['==', ['get', 'classification'], '+'],
										5,
										['==', ['get', 'classification'], '-'],
										5,
										1,
									],
									'circle-opacity': opacityStyle(opacity),
									'circle-opacity-transition': { duration: 0, delay: 0 },
								},
								layout: { visibility },
								filter,
							})}
				/>
			)}
		</DoubleBufferedSource>
	)
}

export const LTZPolygonLayer = ({ isVisible, theme }: CustomLayerProps) => {
	const date = useDebouncedDate()
	const animate = useTimeline((s) => s.isPlaying)
	const { ltzAlertsV2 } = useFlags()
	if (!isLtzShownAt(date, dayjs(), ltzAlertsV2)) return
	const alignedTime = ltzRunTime(date, Boolean(ltzAlertsV2) && animate).toISOString()
	const colorStops = colorscales['lightning-threatzone'][theme]
	const { polygon, horizonAttribute } = ltzLayers(ltzAlertsV2)
	return (
		<DoubleBufferedSource
			key={ltzAlertsV2 ? 'v2' : 'v1'}
			baseLayerId='ltz-polygon'
			forcedRefreshKey={animate}
			tileUrl={`${polygon.url}?TileMatrix=EPSG:900913:{z}&TileCol={x}&TileRow={y}&TIME=${alignedTime}`}
			minzoom={Math.floor(polygon.minZoom)}
			maxzoom={Math.ceil(polygon.minZoom) + 5}
		>
			{(layerId, opacity) => (
				<OrderedLayer
					id={layerId}
					source-layer={polygon.sourceLayer}
					type='fill'
					{...(polygon.filter && { filter: polygon.filter })}
					paint={{
						'fill-color': {
							property: horizonAttribute,
							stops: interpolateColorscale({ 0: colorStops[0], 1: colorStops[1] }, ...legendRanges['lightning-threatzone']),
						},
						'fill-opacity': opacity,
						'fill-opacity-transition': { duration: 0, delay: 0 },
						'fill-outline-color': 'transparent',
					}}
					layout={{
						visibility: isVisible ? 'visible' : 'none',
						'fill-sort-key': ['-', 1000, ['get', horizonAttribute]],
					}}
					minzoom={polygon.minZoom}
				/>
			)}
		</DoubleBufferedSource>
	)
}

export const LTZCircleLayer = ({ isVisible, theme }: CustomLayerProps) => {
	const date = useDebouncedDate()
	const animate = useTimeline((s) => s.isPlaying)
	const { ltzAlertsV2 } = useFlags()
	if (!isLtzShownAt(date, dayjs(), ltzAlertsV2)) return
	const alignedTime = ltzRunTime(date, Boolean(ltzAlertsV2) && animate).toISOString()
	const { centroid } = ltzLayers(ltzAlertsV2)
	return (
		<DoubleBufferedSource
			key={ltzAlertsV2 ? 'v2' : 'v1'}
			baseLayerId='ltz-centroid'
			forcedRefreshKey={animate}
			tileUrl={`${centroid.url}?TileMatrix=EPSG:900913:{z}&TileCol={x}&TileRow={y}&TIME=${alignedTime}`}
			minzoom={0}
			maxzoom={18}
		>
			{(layerId, opacity) => (
				<OrderedLayer
					id={layerId}
					source-layer={centroid.sourceLayer}
					type='circle'
					paint={{
						'circle-color': colorscales['lightning-threatzone'][theme][0],
						'circle-opacity': opacity,
						'circle-opacity-transition': { duration: 0, delay: 0 },
						'circle-radius': 4,
					}}
					layout={{
						visibility: isVisible ? 'visible' : 'none',
					}}
				/>
			)}
		</DoubleBufferedSource>
	)
}

export const LTZLineLayer = ({ isVisible }: CustomLayerProps) => {
	const date = useDebouncedDate()
	const animate = useTimeline((s) => s.isPlaying)
	const { ltzAlertsV2 } = useFlags()
	if (!isLtzShownAt(date, dayjs(), ltzAlertsV2)) return
	const alignedTime = ltzRunTime(date, Boolean(ltzAlertsV2) && animate).toISOString()
	const { velocity } = ltzLayers(ltzAlertsV2)
	return (
		<DoubleBufferedSource
			key={ltzAlertsV2 ? 'v2' : 'v1'}
			baseLayerId='ltz-velocity'
			forcedRefreshKey={animate}
			tileUrl={`${velocity.url}?TileMatrix=EPSG:900913:{z}&TileCol={x}&TileRow={y}&TIME=${alignedTime}`}
		>
			{(layerId, opacity) => (
				<OrderedLayer
					id={layerId}
					source-layer={velocity.sourceLayer}
					type='line'
					paint={{
						'line-color': '#6F7988',
						'line-opacity': opacity,
						'line-opacity-transition': { duration: 0, delay: 0 },
						'line-dasharray': [2, 4],
						'line-width': 1.5,
					}}
					layout={{
						visibility: isVisible ? 'visible' : 'none',
					}}
					minzoom={velocity.minZoom}
				/>
			)}
		</DoubleBufferedSource>
	)
}

const PULSE_HALF_PERIOD_MS = 500
const PULSE_MIN_OPACITY = 0.25
const PULSE_MAX_OPACITY = 1
const REDUCED_MOTION_OPACITY = 0.6

export const LightningAlertRings = ({ isVisible }: CustomLayerProps) => {
	const theme = useTheme()
	const mapTheme = useMapTheme()
	const { locations } = useFilteredLocations()
	const { data: lightningProts = [] } = useLightningProtections()
	const activeLightningProts = useActiveOnTimelineLightningAlertState()
	const activeRealtimeLightningProts = useMemo(() => lightningProts.filter((p) => p.parameterType === 'realtime'), [lightningProts])
	const audibleEvents = useAudibleEvents((s) => s.events)

	const geojson = useMemo(() => {
		const filteredLocationsMap = new Map(locations.filter(isAssetLocation).map((loc) => [loc.assetVrn, loc]))
		const activeProtsMap = new Map(activeLightningProts.map((p) => [p.protection.vrn, p]))
		return featureCollection(
			activeRealtimeLightningProts
				.filter((p) => filteredLocationsMap.has(p.assetVrn))
				.map((p) => {
					const activeProt = activeProtsMap.get(p.vrn)
					return feature(p.geometry, {
						active: !!activeProt,
						audible: !!activeProt && audibleEvents.has(p.vrn),
						color: activeProt?.severity ? mapTheme.alerts[activeProt.severity] : mapTheme.alerts.clear,
						lineWidth: activeProt ? theme.alerts.width[activeProt.severity] : 2,
					})
				}),
		)
	}, [activeRealtimeLightningProts, activeLightningProts, audibleEvents, theme, mapTheme, locations])

	const anyAudible = !!isVisible && geojson.features.some((f) => f.properties.audible)
	const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
	const [pulseHigh, setPulseHigh] = useState(true)

	useEffect(() => {
		if (!anyAudible || reducedMotion) return
		setPulseHigh(true)
		const id = setInterval(() => setPulseHigh((high) => !high), PULSE_HALF_PERIOD_MS)
		return () => clearInterval(id)
	}, [anyAudible, reducedMotion])

	const pulseOpacity = !anyAudible ? 0 : reducedMotion ? REDUCED_MOTION_OPACITY : pulseHigh ? PULSE_MAX_OPACITY : PULSE_MIN_OPACITY

	return (
		<Source id='lightning-alert-rings-source' type='geojson' data={geojson}>
			<OrderedLayer
				id='lightning-alert-rings-pulse'
				type='line'
				filter={['==', ['get', 'audible'], true]}
				paint={{
					'line-width': ['+', ['get', 'lineWidth'], 14],
					'line-color': ['get', 'color'],
					'line-blur': 8,
					'line-opacity': pulseOpacity,
					'line-opacity-transition': { duration: PULSE_HALF_PERIOD_MS },
				}}
				layout={{ visibility: isVisible ? 'visible' : 'none' }}
			/>
			<OrderedLayer
				id='lightning-alert-rings'
				type='line'
				paint={{
					'line-width': ['get', 'lineWidth'],
					'line-color': ['get', 'color'],
					'line-dasharray': ['case', ['get', 'active'], ['literal', [1, 0]], ['literal', [2, 2]]],
				}}
				layout={{ visibility: isVisible ? 'visible' : 'none' }}
			/>
		</Source>
	)
}

export const LightningPointAnimated = ({ isVisible, theme }: CustomLayerProps) => {
	const LTG_POINT_ANIMATION_LAYER = 'lightning-realtime-point-1'
	const date = useDebouncedDate()
	const startTime = useTimeline((s) => s.startTime)
	const endTime = useTimeline((s) => s.endTime)
	const { maxAge } = useLayerInput('lightning-realtime')
	const now = dayjs()
	if (!startTime || !endTime || !date || startTime.isAfter(now) || date.isAfter(now)) return

	const time = getAlignedTime(0, LRT_ANIMATED_INTERVAL * 60, 's', date).valueOf() // align to animation interval

	const endpointEndTime = endTime.isAfter(now) ? now : endTime
	const alignOffset = endpointEndTime.isAfter(now.subtract(1, 'minute')) ? 60_000 : 0

	const startTimeIso = toTileTimeIso(getAlignedTime(alignOffset, LRT_ANIMATED_INTERVAL * 60_000, 'ms', startTime))
	const endTimeIso = toTileTimeIso(getAlignedTime(alignOffset, LRT_ANIMATED_INTERVAL * 60_000, 'ms', endpointEndTime))

	return (
		<Source
			id={`${LTG_POINT_ANIMATION_LAYER}-animated-source`}
			type='vector'
			tiles={[`${env.LTG_RT_POINT_ANIMATED}?startTime=${startTimeIso}&endTime=${endTimeIso}`]}
			maxzoom={LIGHTNING_ZOOM_BOUNDARY}
			minzoom={LIGHTNING_ZOOM_BOUNDARY - 1}
		>
			<OrderedLayer
				id={LTG_POINT_ANIMATION_LAYER}
				type='circle'
				minzoom={LIGHTNING_ZOOM_BOUNDARY - 1}
				paint={{
					'circle-radius': ['case', ['==', ['get', 'cloud'], true], 3, 5],
					'circle-opacity': ['step', ['zoom'], 0.01, LIGHTNING_ZOOM_BOUNDARY, 1],
					'circle-color': [
						'step',
						['-', Math.floor(time / 1000), ['to-number', ['get', 'event_time']]],
						colorscales['lightning-realtime'][theme][0], // Base color (before first stop)
						...interpolateColorscaleFlat(colorscales['lightning-realtime'][theme], 0, maxAge, (val) => Math.round(val) * 60),
					],
				}}
				layout={{
					visibility: isVisible ? 'visible' : 'none',
				}}
				filter={[
					'all',
					['>=', ['to-number', ['get', 'event_time']], Math.floor(new Date(time - maxAge * 60 * 1000).getTime() / 1000)],
					['<=', ['to-number', ['get', 'event_time']], time / 1000],
				]}
				source-layer='LTG'
			/>
		</Source>
	)
}

export const LightningGridAnimated = ({ isVisible, theme }: CustomLayerProps) => {
	const { maxAge } = useLayerInput('lightning-realtime')

	const date = useDebouncedDate()
	const startTime = useTimeline((s) => s.startTime)
	const endTime = useTimeline((s) => s.endTime)
	const now = dayjs()
	if (!startTime || !endTime || !date || startTime.isAfter(now) || date.isAfter(now)) return

	const endpointEndTime = endTime.isAfter(now) ? now : endTime
	const alignOffset = endpointEndTime.isAfter(now.subtract(1, 'minute')) ? 60_000 : 0
	const alignedStartDate = getAlignedTime(alignOffset, LRT_ANIMATED_INTERVAL * 60_000, 'ms', startTime)
	const alignedEndDate = getAlignedTime(alignOffset, LRT_ANIMATED_INTERVAL * 60_000, 'ms', endpointEndTime)
	if (alignedStartDate.isSame(alignedEndDate)) return

	const startTimeIso = toTileTimeIso(alignedStartDate)
	const endTimeIso = toTileTimeIso(alignedEndDate)

	const getFrameKey = (frame: number) => `timestamps.${frame}`
	const frameDate = getAlignedTime(0, LRT_ANIMATED_INTERVAL * 60_000, 'ms', date)
	const diffMins = Math.floor((frameDate.valueOf() - alignedStartDate.valueOf()) / 60_000)
	const frameKey = getFrameKey(Math.floor(diffMins / 6) - 1)

	const filter: mapboxgl.FilterSpecification = maxAge === 60 ? ['has', frameKey] : ['all', ['has', frameKey], ['<=', ['get', frameKey], 30]]
	return (
		<Source
			id='lightning-realtime-grid-source-animated'
			type='vector'
			tiles={[`${env.LTG_RT_GRID_ANIMATED}?TileMatrix=EPSG:900913:{z}&TileCol={x}&TileRow={y}&TIME=${startTimeIso}/${endTimeIso}`]}
			promoteId={{ LD: 'locHash' }}
		>
			<OrderedLayer
				id='lightning-realtime-grid-1'
				maxzoom={LIGHTNING_ZOOM_BOUNDARY}
				type='fill'
				paint={{
					'fill-opacity': 1,
					'fill-antialias': true,
					'fill-outline-color': 'rgba(255, 255, 0, 0.0)',
					'fill-color': [
						'step',
						['get', frameKey],
						colorscales['lightning-realtime'][theme][0], // Base color (before first stop)
						...interpolateColorscaleFlat(colorscales['lightning-realtime'][theme], 0, maxAge, (val) => Math.round(val)),
					],
				}}
				source-layer='LD_agg_animation'
				layout={{ visibility: isVisible ? 'visible' : 'none' }}
				filter={filter}
			/>
		</Source>
	)
}

export const CustomLayers = () => {
	const { data: pref } = usePreferences()
	const mapTheme = useMapState((s) => (s.mapStyle === 'light' ? 'light' : 'dark'))
	const isNowLocked = useTimeline((s) => s.nowLocked)
	const isPlaying = useTimeline((s) => s.isPlaying)
	const isDragging = useTimeline((s) => s.isDragging)
	const customLayers = useCustomLayers((s) => s.customLayers)
	const layers = useSearch({ from: '/_app', select: (s) => s.layers }) ?? []
	return (
		<>
			{Object.values(customLayers).map(({ animate, render, lvId, animatable }) =>
				(isPlaying || isDragging) && animate
					? animate({ isVisible: layers.includes(lvId), theme: mapTheme, pref })
					: render({ isVisible: layers.includes(lvId) && (isNowLocked || animatable), theme: mapTheme, pref }),
			)}
		</>
	)
}
